import { Component, ChangeDetectionStrategy } from '@angular/core';
import { FormBuilder, FormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { NgbActiveModal, NgbModalOptions } from '@ng-bootstrap/ng-bootstrap';
import { TranslateService } from '@ngx-translate/core';
import { EnvironmentEnum } from 'src/app/constants/environment.enum';
import { MarkFormGroupTouchedAndDirty } from 'src/app/directives/mark-form-group-as-touched-dirty.function';
import { MinimumWordCountValidator } from 'src/app/directives/minimum-word-count-validator.directive';
import { User } from 'src/app/models/user';
import { AuthenticationService } from 'src/app/services/authentication.service';
import { JiraServicedesk } from 'src/app/services/external/jira.servicedesk';
import { ToastService } from 'src/app/services/toast.service';
import { environment } from 'src/environments/environment';

export const CreateSupportTicketModalOptions: NgbModalOptions = {
  keyboard: false,
  centered: true,
  size: 'lg',
  backdrop: 'static'
};

@Component({
    templateUrl: './create-support-ticket.modal.html',
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class CreateSupportTicketModal {
  private user: User;
  public summary: string = "";
  public description: string = "";
  public attachments: any[] = [];
  public temporaryAttachmentIds: string[] = [];
  public cclist: string[] = [];
  public form: UntypedFormGroup;
  public summaryControl: FormControl;
  public descriptionControl: FormControl;
  public translations: any = {};
  

  /**
   * Whether the send button has been clicked, prevents spam
   */
  public isMakingSupportTicket = false;

  constructor(private jira: JiraServicedesk,
    private authenticationService: AuthenticationService,
    private activeModal: NgbActiveModal,
    private router: Router,
    private toastService: ToastService,
    private translateService: TranslateService,
    private formBuilder: FormBuilder) {
      this.getTranslations();
      translateService.onLangChange.subscribe(() => {
        this.getTranslations();
      });
      this.user = this.authenticationService.currentUserValue;

      this.summaryControl = new FormControl('',
        [
          Validators.required,
          Validators.minLength(4),
          Validators.maxLength(240),
          MinimumWordCountValidator(1)
        ]
      );
      this.descriptionControl = new FormControl('',
        [
          Validators.required,
          Validators.minLength(4),
          Validators.maxLength(30000),
          MinimumWordCountValidator(1)
        ]
      );
      this.form = this.formBuilder.group(
        {
          supportSummaryControl: this.summaryControl,
          supportDescriptionControl: this.descriptionControl,
        }
      );
  }

  public close() {
    this.activeModal.close();
  }

  public async sendSupportTicket() {
    if (this.form.invalid) {
      MarkFormGroupTouchedAndDirty(this.form);
      return;
    }
    if (this.isMakingSupportTicket) {
      return;
    }
    this.isMakingSupportTicket = true;
    if (this.attachments.length > 0) {
      await this.createTemporaryAttachments();
    }
    this.createSupportTicket();
  }

  // Add files via file input
  public onFilesSelected(event: Event) {
    const input = event.target as HTMLInputElement;
    if (input.files) {
      for (let i = 0; i < input.files.length; i++) {
        const file = input.files.item(i);
        if (file) {
          this.attachments.push(file);
        }
      }
    }
  }

  // Add files via drag & drop
  public onFilesDropped(event: DragEvent) {
    event.preventDefault();
    if (event.dataTransfer && event.dataTransfer.files) {
      for (let i = 0; i < event.dataTransfer.files.length; i++) {
        const file = event.dataTransfer.files.item(i);
        if (file) {
          this.attachments.push(file);
        }
      }
    }
  }

  // Remove an attachment from the list
  public removeAttachment(index: number) {
    this.attachments.splice(index, 1);
  }
  

  private getTranslations() {
    this.translateService.get([
      'Toasts.Success',
      'Toasts.Error',
      'Toasts.SupportTicketCreatedSuccessfully',
      'Toasts.ErrorDuringSupportTicketCreation',
      'Toasts.ErrorDuringUploadingAttachments'
    ]).subscribe(translation => {
      this.translations.toastsSuccess = translation['Toasts.Success'];
      this.translations.toastsError = translation['Toasts.Error'];
      this.translations.toastsSupportTicketCreatedSuccessfully = translation['Toasts.SupportTicketCreatedSuccessfully'];
      this.translations.toastsErrorDuringSupportTicketCreation = translation['Toasts.ErrorDuringSupportTicketCreation'];
      this.translations.toastsErrorDuringUploadingAttachments = translation['Toasts.ErrorDuringUploadingAttachments'];
    });
  }

  private getSummaryWithEnvironment() {
    let summary = this.form.get('supportSummaryControl')?.value || "";
    switch (environment.platformname) {
      case EnvironmentEnum.Development:
        return "DEV - " + summary;
      case EnvironmentEnum.Test:
        return "TEST - " + summary;
      case EnvironmentEnum.Staging:
        return "ACC - " + summary;
      case EnvironmentEnum.Production:
      default:
        return summary;
    }
  }

  private getRequestIdFromUrl(url: string): string {
    const match = url.match(/(?:build|request|view|download)\/([a-f0-9-]{36})/i);
    return match ? match[1] : "";
  }

  private async createSupportTicket() {
    this.jira.createSupportTicket(
      this.getSummaryWithEnvironment(),
      this.form.get('supportDescriptionControl')?.value,
      this.user.email,
      Number(this.user.phoneNumber),
      this.getRequestIdFromUrl(this.router.url),
      window.location.href,
      this.user.organisation ?? "",
      this.temporaryAttachmentIds,
      this.cclist
    ).subscribe({
      next: () => {
        this.toastService.showSuccess(this.translations.toastsSuccess, this.translations.toastsSupportTicketCreatedSuccessfully);
        this.activeModal.close();
      },
      error: (error) => {
        this.isMakingSupportTicket = false;
        this.toastService.showError(this.translations.toastsError, this.translations.toastsErrorDuringSupportTicketCreation);
        console.error('Error creating support ticket:', error);
      }
    });
  }

  private async createTemporaryAttachments(): Promise<string[]> {
    const formData = new FormData();
    this.attachments.forEach(attachment => {
      if (attachment) {
        formData.append('file', attachment, attachment.name);
      }
    });
    return new Promise((resolve, reject) => {
      this.jira.createAttachments(formData).subscribe({
        next: (res: any) => {
          const ids = res.temporaryAttachments?.map((a: any) => a.temporaryAttachmentId) || [];
          this.temporaryAttachmentIds = ids;
          resolve(ids);
        },
        error: (error) => {
          this.toastService.showError(this.translations.toastsError, this.translations.toastsErrorDuringUploadingAttachments);
          reject(error);
        }
      });
    });
  }
}